import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, CircularProgress, Box, Grid } from '@mui/material';
import { Warning as WarningIcon, Error as ErrorIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import axios from 'axios';

const SimplyNodeStatusTile = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = `${process.env.REACT_APP_API_URL}/system-info/simplynode`;
  const API_KEY = process.env.REACT_APP_API_KEY;

  const fetchSimplyNodeData = async () => {
    try {
      const response = await axios.get(API_URL, {
        headers: {
          'x-api-key': API_KEY
        }
      });
      setData(response.data);
    } catch (err) {
      console.error('Error fetching SimplyNode data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSimplyNodeData();
    const interval = setInterval(fetchSimplyNodeData, 300000); // 5 minutes refresh
    return () => clearInterval(interval);
  }, []);

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">Error loading SimplyNode status</Typography>;

  const subaccounts = data?.subaccounts || [];

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h5" component="div" sx={{ mb: 2, fontWeight: 'bold' }}>
          SimplyNode Status
        </Typography>
        
        {subaccounts.length === 0 ? (
          <Typography color="text.secondary">No subaccounts found.</Typography>
        ) : (
          <Grid container spacing={2}>
            {subaccounts.map((sub, index) => {
              const rawAvail = sub.trafficAvailable?.value || 0;
              const unit = sub.trafficAvailable?.unit || 'GB';
              const availGB = unit.toLowerCase() === 'byte' ? rawAvail / 1073741824 : rawAvail;
              
              // Resolve Display Name: use local proxyName alias if matched in DB, else subaccountLogin
              const displayName = sub.proxyName || sub.subaccountLogin;

              let statusColor = 'success.main';
              let StatusIcon = CheckCircleIcon;
              let statusText = 'Healthy';
              let isUrgent = false;
              let isLow = false;

              if (availGB <= 0) {
                statusColor = 'error.main';
                StatusIcon = ErrorIcon;
                statusText = 'Urgent Action Needed';
                isUrgent = true;
              } else if (availGB < 1.0) {
                statusColor = 'warning.main';
                StatusIcon = WarningIcon;
                statusText = 'Low Credits';
                isLow = true;
              }

              return (
                <Grid item xs={12} sm={6} key={sub.subaccountLogin || index}>
                  <Box 
                    onClick={() => window.open('https://app.simplynode.io/', '_blank')}
                    sx={{ 
                      p: 1.5, 
                      border: '1px solid', 
                      borderColor: isUrgent ? 'error.light' : isLow ? 'warning.light' : 'divider', 
                      borderRadius: 1,
                      backgroundColor: isUrgent ? '#fff5f5' : isLow ? '#fffbeb' : 'background.paper',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                          {displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Product: {sub.productType || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: statusColor }}>
                        <StatusIcon fontSize="small" />
                        <Typography variant="caption" sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '10px' }}>
                          {statusText}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="body1" color={isUrgent ? 'error.main' : isLow ? 'warning.main' : 'text.primary'} sx={{ fontWeight: 'bold' }}>
                      {availGB.toFixed(2)} GB remaining
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default SimplyNodeStatusTile;
